import {Link} from "react-router-dom";
import {consoleLog,toNumber} from '../../core/utilities/Utilities.js';
import {useTheme,Avatar,Button,Typography,Stack,Box,Tooltip} from '@mui/material';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import {useNotifications} from '../components/Notification/NotificationsContext';
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fi';
import 'dayjs/locale/pt-br';
import Switch from '@mui/material/Switch';
import LinkMUI from '@mui/material/Link';
import MyAnalysisStatusChip,{resolveStatusCode} from '../components/AnalysisRequest/Consumer/MyAnalysisStatusChip.js';
const file='Entities.js';

const NotificationTypeIcon=({type_db})=>{
    const {getNotificationIcon}=useNotifications();
    return getNotificationIcon(type_db);
};
const NotificationTypeText=({type_db,type})=>{
    const {getNotificationColor}=useNotifications();
    return <Typography
        variant="body2"
        sx={{
            color:getNotificationColor(type_db),
            display:"flex",
            alignItems:"center",
            lineHeight:"inherit"
        }}>{type}</Typography>
};

const makeGridDateValueGetter=()=>(value)=>value?new Date(value):null;

const makeGridDateFormatter=(application,type="date")=>(value)=>{
    if(!value) return "";
    const locale=application?.globalData?.locale?.toLowerCase()||"en";
    return dayjs(value).locale(locale).format(type==="datetime"?"L LT":"L");
};

const ThemedLink=({to,onClick,children,openNewTab=false})=>{
  const theme=useTheme();
  const style={
    textDecoration:"none",
    color:theme.list_link_color,
    fontWeight:'normal'
  };

  return (
    <Link
        to={to}
        target={openNewTab?"_blank":undefined}
        rel={openNewTab?"noopener noreferrer":undefined}
        onClick={onClick}
        style={style}>
      {children}
    </Link>
  );
};

const BooleanIcons=(params,invertLogic=false)=>{
    const isTrue=invertLogic?!params.value:params.value;
    if(params.value===true||params.value===false){
        return isTrue
            ?<CheckCircleIcon sx={{color:'success.main'}}/>
            :<CancelIcon sx={{color:'error.main'}}/>;
    }
    return null;
};

const withEditableIcon=(col)=>{
  if(!col.editable) return col;
  return {
    ...col,
    renderHeader:(params)=>(
      <span style={{display:"flex",alignItems:"center",gap:6}}>
        {params.colDef.headerName}
        <EditIcon fontSize="inherit" style={{opacity:0.35}}/>
      </span>
    )
  };
};

const renderAuditBooleanToSwitch=(params)=>{
    const value=params.value;
    const isBoolean=params.row.is_boolean;
    if(isBoolean){return renderBooleanToSwitch(value);}
    if(value===null||value===undefined) {return <span></span>;}
    return <span>{String(value)}</span>;
}

const renderBooleanToSwitch=(value)=>{
    return <Switch checked={!!value} disabled size="small" />;
}

const renderAccountStatus=(params,application)=>{
    const STATUS=params.row.status&&application.globalData.list_of_values_reverse.Account_Status[params.row.status];
    let color="inherit";
    if(STATUS==="ACTIVE") color="darkgreen";
    else if(STATUS==="INACTIVE") color="darkred";
    return (
        <Box
            sx={{
                width:'100%',
                height:'100%',
                display:'flex',
                alignItems:'center',
                pl:1,
                color,
            }}>
            {params.row.status}
        </Box>
    );
}

// Same color scheme the consumer's own status chip already uses
// (MyAnalysisStatusChip.js - DRAFT no color, SUBMITTED orange, ACCEPTED
// blue, COMPLETED green, REJECTED red) rather than a separate coach/admin
// convention - reused directly instead of duplicated.
const renderAnalysisRequestStatus=(params,application)=>{
    const statusCode=resolveStatusCode(params.row.status,application);
    return (
        <Box sx={{width:'100%',height:'100%',display:'flex',alignItems:'center',pl:1}}>
            <MyAnalysisStatusChip statusCode={statusCode} application={application} size="small"/>
        </Box>
    );
}

export const handleLinkClick=(event)=>{
    const link=event.currentTarget.getAttribute("href");
    consoleLog({file:file,context:'handleLinkClick',arg_name:'link',arg_value:link});
};

const getLocale=(application,String)=>{
  const globalData=application?.globalData;
  if (!globalData||!('lang' in globalData)){
    return 'MISSING_LANG_IN_GLOBAL_DATA';
  }
  const {strings}=globalData;
  if(!strings||!(String in strings)){
    return `MISSING_${String}_IN_STRINGS`;
  }
  return strings[String];
};

export const columns={
    accounts:function(list_properties,application){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,
                renderCell:(params)=>
                    <Box sx={{display:'flex',alignItems:'center',gap:1,height:'100%',minWidth:0}}>
                        {/* 64px thumb of the linked login's profile picture (system_user.profile_picture_thumb), header-avatar size */}
                        <Avatar src={params.row.picture_thumb||undefined} alt="" sx={{width:24,height:24,flexShrink:0,bgcolor:'action.hover',color:'text.disabled'}}>
                            <PersonRoundedIcon sx={{fontSize:18}}/>
                        </Avatar>
                        <ThemedLink to={'/account/'+params.row.id+'/analysis_requests'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
                    </Box>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>renderAccountStatus(params,application),
            },
            {field:'phone',headerName:getLocale(application,'FieldPhone'),width:list_properties?.phone?.width??150,editable:false,},
            {field:'email',headerName:getLocale(application,'FieldEmail'),width:list_properties?.email?.width??200,editable:false,},
            {field:'app_access',headerName:getLocale(application,'FieldAppAccess'),width:list_properties?.app_access?.width??130,editable:false,},
            {field:'city',headerName:getLocale(application,'FieldCity'),width:list_properties?.city?.width??150,editable:false,},
        ]);
    },
    // Account picker on AnalysisRequest's New page (AccountPickListPopup.js)
    account_picklist_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false,renderCell:(params)=><LinkMUI component="button" variant="body2" onClick={()=>handleClickEvent({action:'selectRow',id:params.row.id,pickMap:{name:params.row.name}})}>{params.row.name}</LinkMUI>},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>renderAccountStatus(params,application),
            },
            {field:'app_access',headerName:getLocale(application,'FieldAppAccess'),width:list_properties?.app_access?.width??130,editable:false,},
            {field:'email',headerName:getLocale(application,'FieldEmail'),width:list_properties?.email?.width??200,editable:false,},
            {field:'city',headerName:getLocale(application,'FieldCity'),width:list_properties?.city?.width??150,editable:false,},
        ]);
    },
    analysis_requests:function(list_properties,application){
        return([
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
                renderCell:(params)=>
                    <ThemedLink to={'/analysis_request/'+params.row.id+'/audit'} onClick={handleLinkClick}>{makeGridDateFormatter(application,"datetime")(params.row.created_at)}</ThemedLink>
            },
            {field:'account_name',headerName:getLocale(application,'FieldAccount'),width:list_properties?.account_name?.width??220,editable:false,
                renderCell:(params)=>params.row.account_id?
                    <ThemedLink to={'/account/'+params.row.account_id+'/analysis_requests'} onClick={handleLinkClick}>{params.row.account_name}</ThemedLink>
                    :(params.row.account_name||'')
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??140,editable:false,
                renderCell:(params)=>renderAnalysisRequestStatus(params,application),
            },
            {field:'video_filename',headerName:getLocale(application,'FieldVideo'),width:list_properties?.video_filename?.width??220,editable:false},
        ]);
    },
    // Same as analysis_requests above, minus the account column - used by the
    // "Analysis Requests" tab on Account's own detail page
    // (AccountAnalysisRequests.js), where the account is already the page's
    // context so repeating it in every row would be redundant.
    account_analysis_requests:function(list_properties,application){
        return([
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
                renderCell:(params)=>
                    <ThemedLink to={'/analysis_request/'+params.row.id+'/audit'} onClick={handleLinkClick}>{makeGridDateFormatter(application,"datetime")(params.row.created_at)}</ThemedLink>
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??140,editable:false,
                renderCell:(params)=>renderAnalysisRequestStatus(params,application),
            },
            {field:'video_filename',headerName:getLocale(application,'FieldVideo'),width:list_properties?.video_filename?.width??220,editable:false},
        ]);
    },
    // Coach/admin-managed additions to the hardcoded L4 assessment engine
    // spec - see AnalysisGuideline model docstring and
    // integrations/ai_analysis/guidelines.py.
    analysis_guidelines:function(list_properties,application){
        return([
            {field:'title',headerName:getLocale(application,'FieldTitle'),width:list_properties?.title?.width??300,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/analysis_guideline/'+params.row.id+'/audit'} onClick={handleLinkClick}>{params.row.title}</ThemedLink>
            },
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value),
            },
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'source_url',headerName:getLocale(application,'FieldSourceUrl'),width:list_properties?.source_url?.width??260,editable:false},
        ]);
    },
    // Row click has nowhere else to go (no drilldown page, no inline grid
    // editing - editable:false throughout), so without this button existing
    // rows were unopenable: addable via the toolbar "+" but never editable or
    // deletable again. The button itself is gated on the class-level
    // can_update (role check); once open, AnalysisReportNotePopup's PageDetail
    // shows/hides its own Update/Delete per-record (report DRAFT vs not).
    analysis_report_caveats:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'text',headerName:getLocale(application,'FieldText'),width:list_properties?.text?.width??500,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    analysis_report_next_filming:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'text',headerName:getLocale(application,'FieldText'),width:list_properties?.text?.width??500,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    analysis_report_scorecard:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'element',headerName:getLocale(application,'FieldElement'),width:list_properties?.element?.width??220,editable:false},
            {field:'verdict',headerName:getLocale(application,'FieldVerdict'),width:list_properties?.verdict?.width??130,editable:false},
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??350,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    analysis_report_metrics:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'metric_name',headerName:getLocale(application,'FieldMetricName'),width:list_properties?.metric_name?.width??220,editable:false},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??150,editable:false},
            {field:'comment',headerName:getLocale(application,'FieldComment'),width:list_properties?.comment?.width??350,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    analysis_report_plan_items:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'block_name',headerName:getLocale(application,'FieldBlockName'),width:list_properties?.block_name?.width??220,editable:false},
            {field:'detail',headerName:getLocale(application,'FieldDetail'),width:list_properties?.detail?.width??400,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    analysis_report_cycle_frames:function(list_properties,application){
        return([
            {field:'timestamp_seconds',headerName:getLocale(application,'FieldTimestampSeconds'),width:list_properties?.timestamp_seconds?.width??150,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/analysis_report_cycle_frame/'+params.row.id+'/image'} onClick={handleLinkClick}>{params.row.timestamp_seconds}</ThemedLink>
            },
            {field:'propulsion_phase',headerName:getLocale(application,'FieldPropulsionPhase'),width:list_properties?.propulsion_phase?.width??150,editable:false},
            {field:'caption',headerName:getLocale(application,'FieldCaption'),width:list_properties?.caption?.width??350,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    analysis_report_faults:function(list_properties,application){
        return([
            {field:'title',headerName:getLocale(application,'FieldTitle'),width:list_properties?.title?.width??350,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/analysis_report_fault/'+params.row.id+'/drills'} onClick={handleLinkClick}>{params.row.title}</ThemedLink>
            },
            {field:'priority_order',headerName:getLocale(application,'FieldPriorityOrder'),width:list_properties?.priority_order?.width??110,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'expected_return',headerName:getLocale(application,'FieldExpectedReturn'),width:list_properties?.expected_return?.width??150,editable:false},
        ]);
    },
    analysis_report_fault_drills:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'drill_name',headerName:getLocale(application,'FieldDrillName'),width:list_properties?.drill_name?.width??220,editable:false},
            {field:'prescription',headerName:getLocale(application,'FieldPrescription'),width:list_properties?.prescription?.width??400,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    // The 10 L4 assessment-engine variables - drilldown to the variable's
    // drills grid. Real editing is on the Full Report page.
    analysis_report_variables:function(list_properties,application){
        return([
            {field:'variable_code',headerName:getLocale(application,'FieldVariableCode'),width:list_properties?.variable_code?.width??110,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/analysis_report_variable/'+params.row.id+'/drills'} onClick={handleLinkClick}>{params.row.variable_code}</ThemedLink>
            },
            {field:'category',headerName:getLocale(application,'FieldCategory'),width:list_properties?.category?.width??130,editable:false},
            {field:'score',headerName:getLocale(application,'FieldScore'),width:list_properties?.score?.width??90,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'confidence',headerName:getLocale(application,'FieldConfidence'),width:list_properties?.confidence?.width??110,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'priority_role',headerName:getLocale(application,'FieldPriorityRole'),width:list_properties?.priority_role?.width??160,editable:false},
        ]);
    },
    analysis_report_variable_drills:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'drill_name',headerName:getLocale(application,'FieldDrillName'),width:list_properties?.drill_name?.width??220,editable:false},
            {field:'prescription',headerName:getLocale(application,'FieldPrescription'),width:list_properties?.prescription?.width??400,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
        ]);
    },
    // Read-only - one row per AI analysis attempt (success or failure,
    // including retries), see database/models/analysis_report_ai_usage.py.
    // This is the profitability-tracking tab: model/tokens/cost/duration per
    // attempt, across every AnalysisReport.
    analysis_report_ai_usage:function(list_properties,application){
        return([
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??170,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            // ANALYSIS / RETRY / REVISION - every run is kept (never deleted on
            // retry), so the report's AI cost total covers all of them.
            {field:'run_type',headerName:getLocale(application,'FieldRunType'),width:list_properties?.run_type?.width??110,editable:false,
                valueGetter:(value)=>application.globalData?.list_of_values?.AnalysisReportAiUsage_RunType?.[value]||value,
            },
            // REVISION rows: the coach's feedback text (full text on hover).
            {field:'revision_feedback',headerName:getLocale(application,'FieldRevisionFeedback'),width:list_properties?.revision_feedback?.width??220,editable:false,sortable:false,
                valueGetter:(value)=>value?.feedback||'',
                renderCell:(params)=>params.value?<Tooltip title={params.value}><span>{params.value}</span></Tooltip>:'',
            },
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false,
                renderCell:(params)=>{
                    // AnalysisReportAiUsage.get_list_formatted returns the raw
                    // status code (SUCCESS / FAILED), not a localized label -
                    // unlike most list endpoints - so compare the code directly
                    // (the previous list_of_values_reverse lookup always missed,
                    // leaving FAILED green).
                    const code=params.row.status;
                    const label=application.globalData?.list_of_values?.AnalysisReportAiUsage_Status?.[code]||code;
                    const color=code==="FAILED"?"darkred":code==="SUCCESS"?"darkgreen":"inherit";
                    return <Box sx={{color,fontWeight:600}}>{label}</Box>;
                },
            },
            {field:'provider',headerName:getLocale(application,'FieldProvider'),width:list_properties?.provider?.width??110,editable:false},
            {field:'model',headerName:getLocale(application,'FieldModel'),width:list_properties?.model?.width??150,editable:false},
            {field:'input_tokens',headerName:getLocale(application,'FieldInputTokens'),width:list_properties?.input_tokens?.width??120,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'output_tokens',headerName:getLocale(application,'FieldOutputTokens'),width:list_properties?.output_tokens?.width??120,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'estimated_cost_usd',headerName:getLocale(application,'FieldEstimatedCostUsd'),width:list_properties?.estimated_cost_usd?.width??140,editable:false,type:'number',align:"left",headerAlign:"left",
                renderCell:(params)=>params.value!==null&&params.value!==undefined?`$${Number(params.value).toFixed(4)}`:'',
            },
            {field:'frame_count',headerName:getLocale(application,'FieldFrameCount'),width:list_properties?.frame_count?.width??110,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'duration_seconds',headerName:getLocale(application,'FieldDurationSeconds'),width:list_properties?.duration_seconds?.width??110,editable:false,type:'number',align:"left",headerAlign:"left",
                renderCell:(params)=>params.value!==null&&params.value!==undefined?Number(params.value).toFixed(1):'',
            },
            {field:'video_size_bytes',headerName:getLocale(application,'FieldVideoSizeBytes'),width:list_properties?.video_size_bytes?.width??120,editable:false,type:'number',align:"left",headerAlign:"left",
                renderCell:(params)=>params.value!==null&&params.value!==undefined?`${(Number(params.value)/(1024*1024)).toFixed(1)} MB`:'',
            },
            {field:'video_duration_seconds',headerName:getLocale(application,'FieldVideoDurationSeconds'),width:list_properties?.video_duration_seconds?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",
                renderCell:(params)=>params.value!==null&&params.value!==undefined?`${Number(params.value).toFixed(1)}s`:'',
            },
            // guidelines_used is a JSON array of {id,title,updated_at} - see
            // integrations/ai_analysis/guidelines.py::load_active_guidelines -
            // shown here as a count with the titles on hover so a report can be
            // traced back to which coach guidelines were active for it.
            {field:'guidelines_used',headerName:getLocale(application,'FieldGuidelinesUsed'),width:list_properties?.guidelines_used?.width??140,editable:false,sortable:false,
                renderCell:(params)=>{
                    let entries=[];
                    try{entries=params.value?JSON.parse(params.value):[];}catch(e){entries=[];}
                    if(!entries.length) return '';
                    const titles=entries.map((g)=>g.title).join(', ');
                    return <span title={titles}>{entries.length}</span>;
                },
            },
        ]);
    },
    audit:function(list_properties,application){
        return([
            {field:'entity_column',headerName:getLocale(application,'AuditField'),width:list_properties?.entity_column?.width??300,editable:false},
            {field:'value_old',headerName:getLocale(application,'AuditOldValue'),width:list_properties?.value_old?.width??300,editable:false,
                renderCell:(params)=>renderAuditBooleanToSwitch(params)},
            {field:'value_new',headerName:getLocale(application,'AuditNewValue'),width:list_properties?.value_new?.width??300,editable:false,
                renderCell:(params)=>renderAuditBooleanToSwitch(params)},
            {field:'created_at',headerName:getLocale(application,'AuditDateTime'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'AuditUser'),width:list_properties?.created_by?.width??300,editable:false},
        ]);
    },
    users:function(list_properties,application){
        return([
            {field:'login',headerName:getLocale(application,'FieldLogin'),width:list_properties?.login?.width??150,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/user/'+params.row.id+'/audit'} onClick={handleLinkClick}>{params.row.login}</ThemedLink>},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??300,editable:false},
            {field:'role',headerName:getLocale(application,'FieldRole'),width:list_properties?.role?.width??180,editable:false},
            {field:'status',headerName:getLocale(application,'FieldStatus'),width:list_properties?.status?.width??110,editable:false},
            {field:'email',headerName:getLocale(application,'FieldEmail'),width:list_properties?.email?.width??220,editable:false},
            {field:'phone',headerName:getLocale(application,'FieldPhone'),width:list_properties?.phone?.width??150,editable:false},
        ]);
    },
    list_of_values:function(list_properties,application,handleClickEvent,entityRules){
        const cols=[
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("code")},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("name")},
            {field:'language',headerName:getLocale(application,'FieldLanguage'),width:list_properties?.language?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("language")},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("value")},
            {field:'sorting_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sorting_order?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("sorting_order"),type:'number',valueGetter:(value)=>toNumber(value)},
        ];
        return cols.map(withEditableIcon);
    },
    locales:function(list_properties,application,handleClickEvent,entityRules){
        const cols=[
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("code")},
            {field:'language',headerName:getLocale(application,'FieldLanguage'),width:list_properties?.language?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("language")},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("value")},
        ];
        return cols.map(withEditableIcon);
    },
    notifications:function(list_properties,application){
        return([
            {field:'system_user_login',headerName:getLocale(application,'FooterUser'),width:list_properties?.system_user_login?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/user/'+params.row.system_user_id+'/audit'} onClick={handleLinkClick}>{params.row.system_user_login}</ThemedLink>
            },
            {field:'title',headerName:getLocale(application,'FieldTitle'),width:list_properties?.title?.width??250,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/notification/'+params.row.id} onClick={handleLinkClick}>{params.row.title}</ThemedLink>
            },
            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??250,editable:false},
            {field:'code',headerName:getLocale(application,'FieldCode'),width:list_properties?.code?.width??250,editable:false},
            {field:'link',headerName:getLocale(application,'FieldLink'),width:list_properties?.link?.width??250,editable:false},
            {field:'table_name',headerName:getLocale(application,'FieldTableName'),width:list_properties?.table_name?.width??250,editable:false},
            {field:'record_id',headerName:getLocale(application,'FieldId'),width:list_properties?.record_id?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'read',headerName:getLocale(application,'FieldRead'),width:list_properties?.read?.width??150,editable:false,type:'boolean',
                renderCell:(params)=>BooleanIcons(params),
            },
            {field:'read_at',headerName:getLocale(application,'FieldReadAt'),width:list_properties?.read_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
        ]);
    },
    my_notifications:function(list_properties,application,handleClickEvent){
        return([
            {field:'type',headerName:getLocale(application,'FieldType'),width:list_properties?.type?.width??180,editable:false,
                renderCell:(params)=>(
                    <Box sx={{display:"flex",alignItems:"center",gap:1}}>
                        <NotificationTypeIcon type_db={params.row.type_db} />
                        <NotificationTypeText type_db={params.row.type_db} type={params.row.type}/>
                    </Box>
                )
            },
            {field:'title',headerName:getLocale(application,'FieldTitle'),width:list_properties?.title?.width??250,editable:false,
                renderCell:(params)=>
                    <ThemedLink
                        onClick={(e)=>{
                            e.stopPropagation();
                            e.preventDefault();
                            handleClickEvent({action:'invoke_method',args:{method:"myNotificationDrilldown",recordId:params.row.id,read:params.row.read}})
                        }
                    }>
                        {params.row.title}
                    </ThemedLink>
            },

            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'read',headerName:getLocale(application,'FieldRead'),width:list_properties?.read?.width??150,editable:false,type:'boolean',
                renderCell:(params)=>BooleanIcons(params),
            },
            {field:'read_at',headerName:getLocale(application,'FieldReadAt'),width:list_properties?.read_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
        ]);
    },
    error_logs:function(list_properties,application,handleClickEvent){
        return([
            {field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??350,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(
                <Stack direction="row" spacing={1}>
                    <Button name="goToRecord" variant="contained" onClick={(e)=>handleClickEvent({action:'invoke_method',args:{method:"goToRecord",recordId:params.row.record_id,tableName:params.row.table_name}})}>{getLocale(application,'ButtonGoToTableRecord')}</Button>
                    <Button name="viewDetail" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonViewDetail')}</Button>
                </Stack>
            ),},
            {field:'record_id',headerName:getLocale(application,'FieldId'),width:list_properties?.record_id?.width??300,editable:false},
            {field:'table_name',headerName:getLocale(application,'FieldTableName'),width:list_properties?.table_name?.width??110,editable:false},
            {field:'source',headerName:getLocale(application,'FieldSource'),width:list_properties?.source?.width??250,editable:false},
            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false},
        ]);
    },
    error_logs_popup:function(list_properties,application,handleClickEvent){
        return([
            {field:'action',headerName:getLocale(application,'FieldAction'),display:'flex',width:list_properties?.action?.width??150,sortable:false,filterable:false,editable:false,
                renderCell:(params)=>(
                    <Button name="viewDetail" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonViewDetail')}</Button>
            ),},
            {field:'source',headerName:getLocale(application,'FieldSource'),width:list_properties?.source?.width??250,editable:false},
            {field:'message',headerName:getLocale(application,'FieldMessage'),width:list_properties?.message?.width??250,editable:false},
            {field:'created_at',headerName:getLocale(application,'FieldCreatedAt'),width:list_properties?.created_at?.width??200,editable:false,type:'date',
                valueGetter:makeGridDateValueGetter(),
                valueFormatter:makeGridDateFormatter(application,"datetime"),
            },
            {field:'created_by',headerName:getLocale(application,'FieldCreatedBy'),width:list_properties?.created_by?.width??150,editable:false},
        ]);
    },
    app_roles:function(list_properties,application,handleClickEvent,entityRules){
        const cols=[
            {field:'role',headerName:getLocale(application,'FieldRole'),width:list_properties?.role?.width??150,editable:false},
            {field:'main_color',headerName:getLocale(application,'FieldMainColor'),width:list_properties?.main_color?.width??120,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("main_color")},
            {field:'list_link_color',headerName:getLocale(application,'FieldListLinkColor'),width:list_properties?.list_link_color?.width??130,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("list_link_color")},
            {field:'landing_page',headerName:getLocale(application,'FieldLandingPage'),width:list_properties?.landing_page?.width??150,editable:entityRules?.can_update&&entityRules?.can_update_list_fields?.includes("landing_page")},
        ];
        return cols.map(withEditableIcon);
    },
    app_configs:function(list_properties,application,handleClickEvent,entityRules){
        const canUpdate=entityRules?.can_update;
        const editable=(field)=>canUpdate&&(entityRules?.can_update_list_fields?.includes(field)??false);
        const cols=[
            {field:'category',headerName:getLocale(application,'FieldCategory'),width:list_properties?.category?.width??160,editable:editable('category')},
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??280,editable:editable('name')},
            {field:'value',headerName:getLocale(application,'FieldValue'),width:list_properties?.value?.width??200,editable:editable('value'),
                renderCell:(params)=>params.row.is_sensitive
                    ?<span style={{letterSpacing:'2px',color:'var(--color-text-secondary,#888)'}}>{'●●●●●●●●'}</span>
                    :params.value
            },
            {field:'value_type',headerName:getLocale(application,'FieldValueType'),width:list_properties?.value_type?.width??100,editable:editable('value_type')},
            {field:'core',headerName:getLocale(application,'FieldCore'),width:list_properties?.core?.width??80,editable:false,type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'ui_available',headerName:getLocale(application,'FieldUiAvailable'),width:list_properties?.ui_available?.width??100,editable:editable('ui_available'),type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'is_sensitive',headerName:getLocale(application,'FieldSensitive'),width:list_properties?.is_sensitive?.width??100,editable:editable('is_sensitive'),type:'boolean',
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'description',headerName:getLocale(application,'FieldDescription'),width:list_properties?.description?.width??300,editable:editable('description')},
        ];
        return cols.map(withEditableIcon);
    },
    app_menus:function(list_properties,application){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu/'+params.row.id+'/items'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'menu_label',headerName:getLocale(application,'FieldMenuLabel'),width:list_properties?.menu_label?.width??200,editable:false},
            {field:'menu_color',headerName:getLocale(application,'FieldMenuColor'),width:list_properties?.menu_color?.width??130,editable:false},
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_items:function(list_properties,application){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu_item/'+params.row.id+'/routes'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'label',headerName:getLocale(application,'FieldLabel'),width:list_properties?.label?.width??200,editable:false},
            {field:'icon_name',headerName:getLocale(application,'FieldIconName'),width:list_properties?.icon_name?.width??150,editable:false},
            {field:'visible',headerName:getLocale(application,'FieldVisible'),width:list_properties?.visible?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_item_routes:function(list_properties,application){
        return([
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false,
                renderCell:(params)=>
                    <ThemedLink to={'/app_menu_item_route/'+params.row.id+'/route_tabs'} onClick={handleLinkClick}>{params.row.name}</ThemedLink>
            },
            {field:'path',headerName:getLocale(application,'FieldPath'),width:list_properties?.path?.width??250,editable:false},
            {field:'is_default',headerName:getLocale(application,'FieldDefault'),width:list_properties?.is_default?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
    app_menu_item_route_tabs:function(list_properties,application,handleClickEvent,entityRules){
        return([
            ...(entityRules?.can_update?[{field:'_edit',headerName:'',width:90,sortable:false,filterable:false,disableColumnMenu:true,
                renderCell:(params)=>(<Button size="small" variant="contained" onClick={(e)=>handleClickEvent({action:'openPopup',id:params.row.id})}>{getLocale(application,'ButtonUpdate')}</Button>),
            }]:[]),
            {field:'name',headerName:getLocale(application,'FieldName'),width:list_properties?.name?.width??200,editable:false},
            {field:'label',headerName:getLocale(application,'FieldLabel'),width:list_properties?.label?.width??200,editable:false},
            {field:'path',headerName:getLocale(application,'FieldPath'),width:list_properties?.path?.width??150,editable:false},
            {field:'is_default',headerName:getLocale(application,'FieldDefault'),width:list_properties?.is_default?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
            {field:'sort_order',headerName:getLocale(application,'FieldSortingOrder'),width:list_properties?.sort_order?.width??130,editable:false,type:'number',align:"left",headerAlign:"left",valueGetter:(value)=>toNumber(value)},
            {field:'active',headerName:getLocale(application,'FieldActive'),width:list_properties?.active?.width??100,editable:false,
                renderCell:(params)=>renderBooleanToSwitch(params.value)
            },
        ]);
    },
};

export const listPreDefaultFilter=(globalData)=>{
    return {
        accounts:{
            items:[
                {
                    field:'status',
                    operator:'isAnyOf',
                    value:[
                        globalData?.list_of_values?.Account_Status?.ACTIVE,
                    ]
                },
            ],
        },
    }
}

export const listPreDefaultSorting={
    //No Pre Defaults So Far!
}
